import React, { useState, useEffect, useContext } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Grid,
	ListItemText,
	Typography,
	CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import useCompanySettings from "../../hooks/useSettings/companySettings";

const useStyles = makeStyles((theme) => ({
	maxWidth: {
		width: "100%",
	},
	sectionTitle: {
		marginBottom: theme.spacing(1),
		fontWeight: 600,
		color: theme.palette.text.primary,
	},
	tagPreview: {
		marginTop: theme.spacing(1),
		padding: theme.spacing(1),
		borderRadius: theme.spacing(0.5),
		border: '1px solid',
		backgroundColor: 'rgba(0, 0, 0, 0.04)',
	},
}));

const ResolveTicketModal = ({ modalOpen, onClose, ticket, tickets = [], setLoading }) => {
	const classes = useStyles();
	const [selectedQueue, setSelectedQueue] = useState("");
	const [selectedTag, setSelectedTag] = useState("");
	const [selectedKanbanTag, setSelectedKanbanTag] = useState("");
	const [availableTags, setAvailableTags] = useState([]);
	const [kanbanTags, setKanbanTags] = useState([]);
	const [loading, setLoadingLocal] = useState(false);
	const [requiredTagSetting, setRequiredTagSetting] = useState("disabled");
	const { user } = useContext(AuthContext);
	const { setTabOpen } = useContext(TicketsContext);
	const { get: getSetting } = useCompanySettings();

	// Pode ser um array de tickets (bulk) ou um único ticket
	const isMultipleTickets = tickets.length > 0;
	const ticketsToProcess = isMultipleTickets ? tickets : [ticket].filter(Boolean);

	useEffect(() => {
		const loadSettings = async () => {
			try {
				fetchTags();
				fetchKanbanTags();
				if (user.queues && user.queues.length === 1) {
					setSelectedQueue(user.queues[0].id);
				}
				
				// Carregar configuração de tag obrigatória
				const setting = await getSetting({ column: "requiredTag" });
				setRequiredTagSetting(setting.requiredTag || "disabled");
			} catch (err) {
				setLoadingLocal(false);
				toastError(err);
			}
		};
		
		if (modalOpen) {
			loadSettings();
		}
	}, [modalOpen]);

	const fetchTags = async () => {
		try {
			const response = await api.get("/tags");
			setAvailableTags(response.data.tags || []);
		} catch (error) {
			console.error('Erro ao carregar tags:', error);
			toastError(error);
		}
	};

	const fetchKanbanTags = async () => {
		try {
			const response = await api.get("/tag/kanban/");
			setKanbanTags(response.data.lista || []);
		} catch (error) {
			console.error('Erro ao carregar tags Kanban:', error);
			toastError(error);
		}
	};

	const handleClose = () => {
		// Não permite fechar se estiver processando
		if (loading) {
			return;
		}
		
		onClose(); // Sem parâmetro para cancelamento
		setSelectedQueue("");
		setSelectedTag("");
		setSelectedKanbanTag("");
		setRequiredTagSetting("disabled");
	};

	const handleDialogClose = (event, reason) => {
		// Impede fechar clicando fora (backdropClick) ou pressionando ESC quando em loading
		if (loading && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
			return;
		}
		handleClose();
	};

	const handleResolveTickets = async () => {
		if (!selectedQueue) {
			toast.error("Selecione uma fila antes de resolver o(s) ticket(s)");
			return;
		}
		// Validação da tag obrigatória
		if (requiredTagSetting === "enabled" && !selectedTag) {
			toast.error("Selecione uma tag antes de resolver o(s) ticket(s)");
			return;
		}
		
		if (!selectedKanbanTag) {
			toast.error("Selecione uma lane Kanban antes de resolver o(s) ticket(s)");
			return;
		}

		setLoadingLocal(true);
		setLoading && setLoading(true);
		
		// Pequeno delay para garantir que o loading apareça antes das operações pesadas
		await new Promise(resolve => setTimeout(resolve, 100));
		
		try {
			// Verificação se é obrigatório ter tag no contato
			// Só verificar se a configuração está ativada E o usuário não selecionou uma tag
			if (requiredTagSetting === "enabled" && !selectedTag) {
				const contactChecks = await Promise.all(
					ticketsToProcess.map(async (ticketItem) => {
						const { data: contactData } = await api.get(`/contacts/${ticketItem.contact.id}`);
						return {
							ticket: ticketItem,
							hasTags: contactData.tags?.length > 0,
							contactName: ticketItem.contact.name
						};
					})
				);

				const contactsWithoutTags = contactChecks.filter(check => !check.hasTags);
				
				if (contactsWithoutTags.length > 0) {
					const message = isMultipleTickets 
						? "Há contato(s) sem nenhuma tag associada."
						: "Este contato não possui nenhuma tag associada.";
					toast.warning(message);
					setLoadingLocal(false);
					setLoading && setLoading(false);
					return;
				}
			}

			if (isMultipleTickets) {
				// Resolver múltiplos tickets usando bulk operations
				const ticketIds = ticketsToProcess.map(t => t.id);
				
				// 1. Atualizar status dos tickets para "closed"
				await api.put("/tickets", {
					ticketIds,
					status: "closed",
					userId: user?.id || null,
					queueId: selectedQueue,
					sendFarewellMessage: false,
					amountUsedBotQueues: 0,
				});

				// Pequeno delay para manter animação fluida
				await new Promise(resolve => setTimeout(resolve, 50));

				// 2. Adicionar tag ao contato (se selecionada)
				if (selectedTag) {
					const contactPromises = ticketsToProcess.map(async (t) => {
						try {
							// Verificar se o contato já tem essa tag
							const { data: contactData } = await api.get(`/contacts/${t.contact.id}`);
							const hasTag = contactData.tags?.some(tag => tag.id === parseInt(selectedTag));
							
							if (!hasTag) {
								await api.put(`/contact-tags/${t.contact.id}/${selectedTag}`);
							}
						} catch (error) {
							console.warn(`Erro ao adicionar tag ao contato ${t.contact.id}:`, error);
						}
					});
					await Promise.all(contactPromises);
				}

				// Pequeno delay para manter animação fluida
				await new Promise(resolve => setTimeout(resolve, 50));

				// 3. Adicionar lane Kanban em massa (substitui tags Kanban existentes)
				await api.post("/ticket-tags/bulk", {
					ticketIds,
					tagId: selectedKanbanTag
				});

			} else {
				// Lógica individual (mantém compatibilidade)
				const t = ticketsToProcess[0];
				
				// 1. Atualizar status do ticket para "closed"
				await api.put(`/tickets/${t.id}`, {
					status: "closed",
					userId: user?.id || null,
					queueId: selectedQueue,
					sendFarewellMessage: false,
					amountUsedBotQueues: 0,
				});

				// Pequeno delay para manter animação fluida
				await new Promise(resolve => setTimeout(resolve, 50));

				// 2. Adicionar tag ao contato (se selecionada)
				if (selectedTag) {
					try {
						// Verificar se o contato já tem essa tag
						const { data: contactData } = await api.get(`/contacts/${t.contact.id}`);
						const hasTag = contactData.tags?.some(tag => tag.id === parseInt(selectedTag));
						
						if (!hasTag) {
							await api.put(`/contact-tags/${t.contact.id}/${selectedTag}`);
						}
					} catch (error) {
						console.warn(`Erro ao adicionar tag ao contato ${t.contact.id}:`, error);
					}
				}
				
				// 3. Adicionar/atualizar lane Kanban
				await api.put(`/ticket-tags/${t.id}/${selectedKanbanTag}`);
			}

			// Pequeno delay antes de fechar para mostrar conclusão
			await new Promise(resolve => setTimeout(resolve, 200));

			setLoadingLocal(false);
			setLoading && setLoading(false);
			onClose("success"); // Passa um valor truthy como a modal de Arquivar
			
			// Feedback
			if (isMultipleTickets) {
				toast.success(`${ticketsToProcess.length} ticket(s) resolvido(s) com sucesso!`);
			} else {
				toast.success("Ticket resolvido com sucesso!");
			}

		} catch (err) {
			setLoadingLocal(false);
			setLoading && setLoading(false);
			console.error("Erro ao resolver ticket(s):", err);
			toastError(err);
		}
	};

	const selectedKanbanTagData = kanbanTags.find(tag => tag.id === selectedKanbanTag);

	return (
		<Dialog open={modalOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
			<DialogTitle>
				{isMultipleTickets ? `Resolver ${ticketsToProcess.length} ticket(s)` : "Resolver Ticket"}
			</DialogTitle>
			<DialogContent dividers style={{ position: 'relative', minHeight: 180 }}>
				{loading && (
					<div style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						background: 'rgba(255,255,255,0.7)',
						zIndex: 10,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						willChange: 'transform',
						backfaceVisibility: 'hidden',
						transform: 'translate3d(0, 0, 0)',
					}}>
						<CircularProgress 
							size={48} 
							color="primary" 
							style={{
								willChange: 'transform',
								backfaceVisibility: 'hidden',
							}}
						/>
					</div>
				)}
				
				<Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
					{isMultipleTickets 
						? `Configure as informações para resolver os ${ticketsToProcess.length} tickets selecionados:`
						: "Configure as informações para resolver este ticket:"
					}
				</Typography>
				<Grid container spacing={2}>
					{/* Fila do atendimento */}
					<Grid item xs={12}>
						<Typography variant="subtitle2" className={classes.sectionTitle}>
							Fila do atendimento
						</Typography>
						<FormControl variant="outlined" className={classes.maxWidth} required>
							<InputLabel>Selecionar fila *</InputLabel>
							<Select
								value={selectedQueue}
								onChange={(e) => setSelectedQueue(e.target.value)}
								label="Selecionar fila *"
							>
								<MenuItem value="">
									<em>Selecione uma fila</em>
								</MenuItem>
								{(user.queues || []).map((queue) => (
									<MenuItem key={queue.id} value={queue.id}>
										<ListItemText primary={queue.name} />
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					{/* Tag do Contato */}
					<Grid item xs={12}>
						<Typography variant="subtitle2" className={classes.sectionTitle}>
							Tag do Contato {requiredTagSetting === "enabled" ? "" : "(Opcional)"}
						</Typography>
						<FormControl variant="outlined" className={classes.maxWidth} required={requiredTagSetting === "enabled"}>
							<InputLabel>Selecionar tag</InputLabel>
							<Select
								value={selectedTag}
								onChange={(e) => setSelectedTag(e.target.value)}
								label={`Selecionar tag`}
								renderValue={(value) => {
									if (!value) return "";
									const selectedTagData = availableTags.find(tag => tag.id === value);
									if (!selectedTagData) return "";
									
									return (
										<div style={{
											backgroundColor: selectedTagData.color,
											color: "#FFF",
											padding: "2px 8px",
											borderRadius: "3px",
											fontSize: "0.8em",
											fontWeight: "bold",
											display: "inline-block",
											whiteSpace: "nowrap"
										}}>
											{selectedTagData.name.toUpperCase()}
										</div>
									);
								}}
							>
								<MenuItem value="">
									<em>{requiredTagSetting === "enabled" ? "Selecione uma tag" : "Nenhuma tag"}</em>
								</MenuItem>
								{availableTags.map((tag) => (
									<MenuItem key={tag.id} value={tag.id}>
										<ListItemText primary={tag.name} />
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>

					{/* Lane Kanban */}
					<Grid item xs={12}>
						<Typography variant="subtitle2" className={classes.sectionTitle}>
							Etapa de Compra
						</Typography>
						<FormControl variant="outlined" className={classes.maxWidth} required>
							<InputLabel>Selecionar etapa</InputLabel>
							<Select
								value={selectedKanbanTag}
								onChange={(e) => setSelectedKanbanTag(e.target.value)}
								label="Selecionar lane Kanban"
							>
								<MenuItem value="">
									<em>Selecione uma etapa</em>
								</MenuItem>
								{kanbanTags.map((tag) => (
									<MenuItem key={tag.id} value={tag.id}>
										<ListItemText primary={tag.name} />
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{/* Preview da lane kanban selecionada */}
						{selectedKanbanTagData && (
							<div 
								className={classes.tagPreview}
								style={{ 
									backgroundColor: selectedKanbanTagData.color ? selectedKanbanTagData.color + '20' : 'rgba(0, 0, 0, 0.04)',
									borderColor: selectedKanbanTagData.color || '#ccc'
								}}
							>
								<Typography variant="body2">
									Ticket será categorizado como: <strong>{selectedKanbanTagData.name}</strong>
								</Typography>
							</div>
						)}
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="secondary" variant="outlined" disabled={loading}>
					Cancelar
				</Button>
				<ButtonWithSpinner
					variant="contained"
					color="primary"
					onClick={handleResolveTickets}
					loading={false} // nunca mostra spinner no botão
					disabled={!selectedQueue || !selectedKanbanTag || (requiredTagSetting === "enabled" && !selectedTag) || loading}
				>
					{isMultipleTickets ? `Resolver ${ticketsToProcess.length} ticket(s)` : "Resolver"}
				</ButtonWithSpinner>
			</DialogActions>
		</Dialog>
	);
};

export default ResolveTicketModal; 
